/* globals ENV_CONFIG:false */

import _ from 'lodash';
import { InjectableClass } from '@fino/lib-injection';
import { Environment } from './environment';
import { Tunnels } from './environment.constants';

/**
 * This module serves as a helper to expose the current execution environment.
 */
export class EnvironmentServiceProvider extends InjectableClass {
  constructor($windowProvider) {
    'ngInject';
    super({ $windowProvider });

    // Set environment configuration defined by webpacks DefinePlugin
    this.envConfig = ENV_CONFIG || {};
    this.available();
  }

  get build() {
    return this.envConfig.build;
  }

  get stages() {
    return this.envConfig.stages;
  }

  get custom() {
    return this.envConfig.custom;
  }

  get win() {
    return this.$windowProvider.$get();
  }

  get location() {
    return this.win.location;
  }

  get host() {
    return this.location.hostname;
  }

  get protocol() {
    return this.location.protocol;
  }

  get port() {
    return this.location.port;
  }

  get apiHost() {
    return this.envConfig.apiHost;
  }

  available() {
    return this.getEnvironment();
  }

  setConfig(config) {
    this.envConfig = config;
  }

  getConfig() {
    return this.envConfig;
  }

  // Returns the current environment containing the tenant
  // and the stage which are defined inside the location
  // hostname and have to be parsed out.
  getEnvironment(options, envConfig) {
    // Apply optional envConfig definitions to overwrite dynamically parsed environment
    if (envConfig) {
      this.envConfig = angular.extend(this.envConfig || {}, envConfig);
    }

    // Environment needs only the be parsed once!
    if (!this.environment) {
      this.environment = Environment.create(this.envConfig);
    }

    // Apply given options e.g. to omit specific keys
    if (options) {
      if (options.omit) {
        return _.omit(this.environment, options.omit);
      }
    }

    return this.environment;
  }

  getCustom() {
    return this.environment.custom;
  }

  getTenant() {
    return this.environment.tenant;
  }

  getStage() {
    return this.environment.stage;
  }

  getProject() {
    return this.environment.project;
  }

  setProject(project) {
    if (this.available()) {
      this.environment.project = project;
    }
  }

  getUrlParam(key) {
    const query = this.win.location.search.substring(1);
    const vars = query.split('&');
    for (let i = 0; i < vars.length; i++) {
      let pair = vars[i].split('=');
      if (pair[0] === key) {
        return pair[1];
      }
    }
  }

  getAbsUrl() {
    if (this.isFilehost()) {
      // Read env specific baseUrl from standalone file
      if (this.win._env && this.win._env.baseUrl) {
        return this.win._env.baseUrl;
      }
    }
    // Returns the absolute url (protocol + host)
    // which depends on the current environment.
    if (this.isLocalhost()) {
      return this.apiHost;
    }
    return `${this.protocol}://${this.host}`;
  }

  isCustom(custom) {
    return this.getCustom() === custom;
  }

  isTenant(tenant, checkMultiple = false) {
    // Allow checking for multiple tenants (when given tenant is an array of tenants)
    const currentTenant = this.getTenant();
    if (checkMultiple && angular.isArray(tenant)) {
      return tenant.indexOf(currentTenant) > -1;
    }
    return currentTenant === tenant;
  }

  isEnvironment(env) {
    const stage = this.getStage();
    return stage && this.getStage().indexOf(env) !== -1;
  }

  isProduction() {
    // Not the best solution but in case of an host/environment switch
    // we can not ensure that the production environment detection
    // will work as long as it depends on parts of the host.
    return !(
      this.isTesting() ||
      this.isDevelopment() ||
      this.isLocalhost() ||
      this.isTunnel()
    );
  }

  isTesting() {
    return this.isEnvironment(this.stages.test);
  }

  isDevelopment() {
    return this.isEnvironment(this.stages.dev);
  }

  isLocalhost() {
    return this.host.indexOf('localhost') !== -1;
  }

  isFilehost() {
    return !this.host || this.protocol === 'file';
  }

  /**
   * Prevent tunneled environments from being misinterpreted as production
   */
  isTunnel() {
    return Tunnels.indexOf(this.host) > -1;
  }

  $get() {
    'ngInject';

    const {
      envConfig,
      environment,
      win,
      location,
      host,
      protocol,
      port,
      apiHost,
      build,
      stages,
      custom,
      available,
      setConfig,
      getConfig,
      getEnvironment,
      getCustom,
      getTenant,
      getStage,
      getProject,
      setProject,
      getUrlParam,
      getAbsUrl,
      isCustom,
      isTenant,
      isEnvironment,
      isProduction,
      isTesting,
      isDevelopment,
      isLocalhost,
      isFilehost,
      isTunnel,
    } = this;

    return {
      envConfig,
      environment,
      win,
      location,
      host,
      protocol,
      port,
      apiHost,
      build,
      stages,
      custom,
      available,
      setConfig,
      getConfig,
      getEnvironment,
      getCustom,
      getTenant,
      getStage,
      getProject,
      setProject,
      getUrlParam,
      getAbsUrl,
      isCustom,
      isTenant,
      isEnvironment,
      isProduction,
      isTesting,
      isDevelopment,
      isLocalhost,
      isFilehost,
      isTunnel,
    };
  }
}



// WEBPACK FOOTER //
// ./node_modules/@fino/ng1-environment/src/environmentService.provider.js